'use client';
import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { HelpCircle, Send, X } from 'lucide-react';
import type { BrainReissueTarget } from '../../../src/shared/wireContract';
import { Button } from '../../components/ui/Button';
import { HelpTip } from '../../components/ui/HelpTip';
import { Spinner } from '../../components/ui/states';
import { useOperationFailure } from '../../components/ui/Toast';
import { useTranslation } from '../../lib/i18n';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { ChatTextarea, useChatTextareaSize } from './ChatTextarea';

const EditContext = createContext<((target: BrainReissueTarget, trigger: HTMLButtonElement) => void) | null>(null);
export function useMessageEdit() { return useContext(EditContext); }

/** Scoped to one user bubble; only the authoritative latest target can keep its editor mounted. */
export function MessageEdit({ children, disabled = false }: { children: (editor: ReactNode) => ReactNode; disabled?: boolean }) {
  const { activeSessionId } = useBrainChat();
  const { reissueTarget, busy, readOnly, childFocus, membership } = useBrainChatStatus();
  const [edit, setEdit] = useState<{ target: BrainReissueTarget; session: string | null; trigger: HTMLButtonElement } | null>(null);
  const active = edit && edit.session === activeSessionId && !disabled && membership?.isOwner && !busy && !readOnly && !childFocus
    && reissueTarget?.userMessageId === edit.target.userMessageId
    && reissueTarget?.expectedMessageId === edit.target.expectedMessageId;
  useEffect(() => { if (!active) setEdit(null); }, [active]);
  const cancel = () => {
    setEdit(null);
    edit?.trigger.focus();
  };
  return <EditContext.Provider value={(target, trigger) => setEdit({ target, session: activeSessionId, trigger })}>
    {children(active && reissueTarget ? <InlineEditor target={reissueTarget} cancel={cancel} /> : null)}
  </EditContext.Provider>;
}

function InlineEditor({ target, cancel }: { target: BrainReissueTarget; cancel: () => void }) {
  const { t } = useTranslation();
  const { reissue } = useBrainChat();
  const report = useOperationFailure();
  const failureKey = `message-edit:${useId()}`;
  const caption = useId();
  const note = target.attachments.length ? `${t.brainChat.ux.reissueWarning} ${t.brainChat.ux.attachmentsKept}` : t.brainChat.ux.reissueWarning;
  const [text, setText] = useState(target.text);
  const [pending, setPending] = useState(false);
  const sending = useRef(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useChatTextareaSize(ref, text);
  useLayoutEffect(() => {
    ref.current!.focus({ preventScroll: true });
    const end = ref.current!.value.length;
    ref.current!.setSelectionRange(end, end);
  }, []);
  const submit = async () => {
    if (sending.current || !text.trim()) return;
    sending.current = true;
    setPending(true);
    try {
      // The provider reports refusals and invalidates the target only after acceptance.
      // A resolved Promise alone is not proof of success: keep the draft until that invalidation.
      await reissue(target, text);
    } catch (error) {
      report(failureKey, error, { message: t.common.actionFailed, leave: cancel });
    } finally {
      sending.current = false;
      setPending(false);
    }
  };
  return <form className="chat-message-editor w-full min-w-0 whitespace-normal" aria-busy={pending || undefined}
    onSubmit={event => { event.preventDefault(); void submit(); }}>
    <ChatTextarea ref={ref} variant="full" rows={1} className="block w-full text-current"
      aria-label={t.brainChat.ux.editMessage} aria-describedby={caption} value={text} disabled={pending}
      onChange={event => setText(event.target.value)}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Escape') { event.preventDefault(); if (!sending.current) cancel(); }
        if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form!.requestSubmit(); }
      }} />
    {/* The consequence of replaying history is one HelpTip, and the same text is the textarea's accessible
        description, so it is announced without taking any room in the bubble. */}
    <span id={caption} className="sr-only">{note}</span>
    <div className="mt-2 flex items-center justify-end gap-1">
      <HelpTip align="left" trigger={<HelpCircle size={14} aria-hidden />}>{note}</HelpTip>
      <Button type="button" variant="ghost" size="icon-sm" icon={X} disabled={pending} onClick={cancel}
        aria-label={t.common.cancel} title={t.common.cancel} className="text-current" />
      <Button type="submit" variant="accent" size="icon-sm" icon={pending ? undefined : Send} disabled={pending || !text.trim()}
        aria-label={t.brainChat.ux.editResend} title={t.brainChat.ux.editResend}>
        {pending ? <Spinner tone="text-current" /> : null}
      </Button>
    </div>
  </form>;
}
